import { isNil } from 'lodash-es';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosBody } from 'react-icons/io';
import { Link } from 'react-router';
import { ROUTES } from '../../../constants/Constants';
import useAppNavigation from '../../../hooks/useAppNavigation';
import useUser from '../../../hooks/useUser';

export type LogoProps = {
  size?: number;
  showText: boolean;
  nolink?: boolean;
  alwaysBrand?: boolean;
};

// The logo should be a dynamic image not a static IoIosBody: TODO

const Logo: React.FC<LogoProps> = ({ size, showText, nolink, alwaysBrand }) => {
  const { t } = useTranslation();
  const { currentUser, isPartner } = useUser();
  const { toLocalizedRoute } = useAppNavigation();

  const motto = useMemo<React.ReactNode>(() => {
    return isPartner && !isNil(currentUser) ? (
      <span className="-mt-1 cursor-pointer hover:text-sky-600 hidden md:flex flex-row min-w-full items-center gap-1 justify-start text-start text-sm font-bold leading-none text-slate-600 tracking-tight subpixel-antialiased group-hover:drop-shadow-sm">
        <Link
          to={toLocalizedRoute(ROUTES.PRICES_ROUTE)}
          className="cursor-pointer"
        >
          {t(`static.logo.account_tier_name_${currentUser.account_tier}`)}
        </Link>
      </span>
    ) : (
      <span className="-mt-1 hidden md:block text-sm font-semibold leading-none tracking-tight text-slate-600 subpixel-antialiased group-hover:drop-shadow-sm">
        {t('dynamic.common_company_motto_for_clients')}
      </span>
    );
  }, [t, isPartner, toLocalizedRoute, currentUser]);

  return (
    <span
      id="header-navbar-logo"
      key="header-navbar-logo"
      aria-label={`${t('dynamic.common_company_name')} | ${t(
        'dynamic.common_company_motto_for_clients',
      )}`}
      data-umami-event="header-navbar-logo"
      data-test-id="header-navbar-logo"
      className={`${
        !nolink && 'group'
      } flex flex-row items-center justify-start transition`}
    >
      <span className="rounded-full bg-sky-600 p-2 text-white group-hover:shadow-md">
        <Link
          to={toLocalizedRoute(ROUTES.HOME_ROUTE)}
          className="cursor-pointer"
        >
          <IoIosBody size={size ?? 18} />
        </Link>
      </span>
      {showText && (
        <span
          className={`${
            alwaysBrand !== true ? 'hidden' : 'flex'
          } flex-col items-start justify-start gap-0 px-2 text-left lg:flex`}
        >
          <span className="block text-2xl font-bold tracking-tight text-sky-600 subpixel-antialiased group-hover:drop-shadow-sm">
            <Link
              to={toLocalizedRoute(ROUTES.HOME_ROUTE)}
              className="cursor-pointer"
            >
              {t('dynamic.common_company_name')}
            </Link>
          </span>
          {motto}
        </span>
      )}
    </span>
  );
};

export default Logo;
